import type { CSSProperties } from 'react';
import type { BlockField } from '@/blocks/domain/types';
import { fieldStyleEditsInPlaceAsText } from '@/blocks/domain/helpers/fieldTextStyle';
import { resolveTokens, resolveTokensForHtml } from '../../application/resolveTokens';
import { expandJobSiteFields, expandSectionedJobSiteFields } from '../../application/expandJobSiteFields';
import { keepsUnboundTokens } from '../../application/unboundTokenHtml';
import { blockStyleToCss } from '../../application/blockStyleToCss';
import { TEXT_COLORS } from '@/common/presentation/constants/colors';
import { CompanyLogoSlot } from '../CompanyLogoSlot';
import { readResolvedLogoUrlFromContext } from '../../application/documentBranding';
import { normalizeHeaderSections } from '@/blocks/domain/helpers/headerSections';
import { panelRowName } from '@/blocks/domain/helpers/panelRowName';
import { fieldStyleLabel } from '@/blocks/domain/helpers/fieldStyleLabel';
import { renderFieldElement } from './fields/FieldElementRender';
import { renderEmptyMediaPlaceholder } from './fields/EmptyMediaPlaceholder';
import { unboundValueDisplay } from './fields/unboundTokenDisplay';
import { SECTIONED_ARIA_LABELS, type FieldsBlockRenderProps } from './fields/fieldsBlockRenderTypes';
import { FieldsSectionRender } from './fields/FieldsSectionRender';
import { FlatFieldList } from './fields/FlatFieldList';
import { CanvasElementShell } from '../editing/CanvasElementShell';
import { useCanvasStructure, type CanvasSlot } from '../editing/CanvasStructureContext';
import { EditableFieldText } from '../editing/EditableFieldText';
import { CanvasElementTextToolbar } from '../editing/CanvasElementTextToolbar';
import { useOptionalEditableDocument } from '../editing/EditableDocumentContext';
import { useDocumentSurface } from '@/document-render/presentation/primitives/useDocumentSurface';
import { EDITOR_FIELD_TEXT_METRICS, EXPORT_FIELD_TEXT_METRICS_CSS, FIELD_LIST_EXPORT_PX } from '@/common/domain/fieldListSpacing';

/**
 * Shared renderer for the field-based block kinds (Header / Customer block / Signature / sectioned Custom). Walks the
 * visible fields in sort order, resolves any `{{token}}` placeholders inside `value`, and lays each
 * row out as full-width or half-width with the requested alignment — OR, when the caller (Header
 * only) supplies a real section/column model, lays sections out via `FieldsSectionRender`.
 *
 * All 12 `BlockFieldStyle` values render via `FieldElementRender` (LLAMS-610 Part 7); the PDF
 * mirror is `document-pdf/application/react-pdf/blocks/FieldsBlockPdf.tsx` — see the HTML/PDF
 * parity rule in `.cursor/plans/README.md`.
 */
const MEDIA_OR_DATASET_STYLES: ReadonlySet<BlockField['style']> = new Set(['image', 'logo', 'dataset']);

export function FieldsBlockRender({
  fields,
  style,
  context,
  accentColor,
  knownPaths,
  ariaLabel,
  logoUrl,
  logoSize = 'medium',
  logoAlignment = 'left',
  sections,
  columns,
  renderFieldOverlay,
  isFieldDimmed
}: FieldsBlockRenderProps) {
  const resolveOptions = knownPaths ? { knownPaths } : undefined;
  const wrapperStyle: CSSProperties = blockStyleToCss(style);

  const accent = accentColor ?? (style.textColor || TEXT_COLORS.PRIMARY_ACCENT_FALLBACK);
  const companyLogoUrl = readResolvedLogoUrlFromContext(context);

  const visible = [...fields].filter((f) => f.visible !== false).sort((a, b) => a.sortOrder - b.sortOrder);
  const hasLogo = Boolean(logoUrl?.trim());
  // Header block owns the header logo chrome — keep test id / slot parity with PageRender.
  const logoSlot = ariaLabel === 'block-header' ? 'header' : 'fields';
  const editable = useOptionalEditableDocument();
  const canvasStructure = useCanvasStructure();
  const exported = useDocumentSurface() === 'export';
  const textMetrics = exported ? EXPORT_FIELD_TEXT_METRICS_CSS : EDITOR_FIELD_TEXT_METRICS;

  // Authored sections (header, a sectioned Custom block, or a customer block the editor has promoted).
  // A legacy flat list — no `sections`, columns under 2 — keeps the wrap layout, including imported instances.
  const authoredSections = Boolean(sections?.length) || (columns ?? 0) >= 2;
  const hasRealSections = authoredSections && SECTIONED_ARIA_LABELS.has(ariaLabel);

  const sectionedModel = hasRealSections ? normalizeHeaderSections({ kind: 'header', fields, sections, columns }) : null;
  // Document surface only (never the editor, whose field ids are selectable): a block whose fields
  // reference `{{jobsite.*}}` prints one numbered group per job site when the quote has two or more.
  // A sectioned Bill to / Job site block expands column by column (LLAMS-844); other sectioned kinds are unchanged.
  const sectionedExpansion =
    exported && sectionedModel && ariaLabel === 'block-customer_block'
      ? expandSectionedJobSiteFields(sectionedModel.sections, sectionedModel.fields, context)
      : null;
  const expandedFields = sectionedExpansion ?? (exported && !hasRealSections ? expandJobSiteFields(visible, context) : []);
  const expandedByFieldId = new Map(expandedFields.map((entry) => [entry.field.id, entry]));

  const renderFieldContent = (field: BlockField) => {
    const expanded = expandedByFieldId.get(field.id);
    const fieldContext = expanded?.context ?? context;
    // `rich_text` is injected as HTML, so its substituted values are escaped; every other style is a text node.
    // Sample data off: a rich text keeps its tokens (drawn as chips by the element); the text styles
    // draw theirs through `valueDisplay` below.
    const chipTokens = field.style === 'rich_text' && keepsUnboundTokens(fieldContext, exported ? 'export' : 'editor');
    const resolvedText = (
      field.style === 'rich_text'
        ? resolveTokensForHtml(field.value ?? '', fieldContext, chipTokens ? { ...resolveOptions, keepTokens: true } : resolveOptions)
        : resolveTokens(field.value ?? '', fieldContext, resolveOptions)
    ).trim();
    const resolvedValue = expanded?.prefix && resolvedText ? `${expanded.prefix}${resolvedText}` : resolvedText;
    // Only an element whose value is plain text swaps to the in-place text box. An Image / Logo keeps drawing the picture while it is
    // selected (its source is edited in its settings, or in place when empty), and a Text (rich_text) element keeps drawing its formatted
    // text while the rich editor in its settings changes it: a text box would show the link, or the HTML markup, instead.
    const isEditingThisField = !exported && editable?.selectedElementId === field.id && fieldStyleEditsInPlaceAsText(field.style);
    if (isEditingThisField) {
      return (
        <EditableFieldText
          fieldId={field.id}
          value={field.value ?? ''}
          onCommit={(next) => editable?.onFieldTextChange(field.id, next)}
          onFocusLabel={`Edit ${field.label || fieldStyleLabel(field.style)}`}
          fontSize={style.fontSize}
          align={field.align}
          italic={field.italic}
          uppercase={field.uppercase}
          toolbar={
            canvasStructure && !MEDIA_OR_DATASET_STYLES.has(field.style) ? (
              <CanvasElementTextToolbar
                field={field}
                onPatch={(patch) => canvasStructure.patchElement(field.id, patch)}
                testId={`document-render:field:${field.id}`}
              />
            ) : undefined
          }
          testId={`document-render:field:${field.id}:editable-text`}
        />
      );
    }
    const rendered = renderFieldElement({
      field,
      resolvedValue,
      valueDisplay: unboundValueDisplay(field, fieldContext, exported),
      chipTokens,
      visual: {
        accent,
        fontSize: style.fontSize,
        ...(typeof style.fontWeightValue === 'number' ? { fontWeight: style.fontWeightValue } : {})
      },
      companyLogoUrl,
      metrics: textMetrics
    });
    const showPlaceholder = rendered === null && canvasStructure && !exported;
    const element = showPlaceholder ? renderEmptyMediaPlaceholder(field) : rendered;
    const overlay = renderFieldOverlay?.(field);
    if (!overlay) return element;
    return (
      <div style={{ position: 'relative', opacity: isFieldDimmed?.(field) ? 0.5 : undefined }} data-field-overlay-host={field.id}>
        {element}
        {overlay}
      </div>
    );
  };

  const renderOneField = (field: BlockField, slot?: CanvasSlot) => {
    const content = renderFieldContent(field);
    if (exported) {
      return (
        <div
          key={field.id}
          data-testid={`document-render:export-field:${field.id}`}
          data-field-id={field.id}
          style={{ textAlign: field.align, paddingBottom: FIELD_LIST_EXPORT_PX.sectionedFieldPaddingBottom }}
        >
          {content}
        </div>
      );
    }

    const name = panelRowName(field, {
      categories: editable?.variableCategories,
      typeLabel: fieldStyleLabel(field.style),
      rawToken: false
    });
    return (
      <CanvasElementShell key={field.id} field={field} name={name} slot={slot}>
        <div data-field-id={field.id} style={{ textAlign: field.align, width: '100%' }}>
          {content}
        </div>
      </CanvasElementShell>
    );
  };

  return (
    // A named block needs a role: aria-label on a bare div is prohibited (axe aria-prohibited-attr).
    <div style={wrapperStyle} role="group" aria-label={ariaLabel}>
      {hasLogo && logoUrl ? <CompanyLogoSlot url={logoUrl} size={logoSize} alignment={logoAlignment} slot={logoSlot} /> : null}

      {sectionedModel ? (
        <FieldsSectionRender
          sections={sectionedModel.sections}
          fields={sectionedExpansion ? sectionedExpansion.map((entry) => entry.field) : sectionedModel.fields}
          renderField={renderOneField}
        />
      ) : (
        <FlatFieldList
          fields={exported ? expandedFields.map((entry) => entry.field) : visible}
          exported={exported}
          renderEditorField={renderOneField}
          renderExportContent={renderFieldContent}
        />
      )}
    </div>
  );
}
